import React, { useEffect, useState } from 'react';
import AppWarehouse from './AppWarehouse';
import AppMultiTenant from './AppMultiTenant';
import PurchasePage from './pages/PurchasePage';
import PurchaseSuccess from './pages/PurchaseSuccess';
import MyAccount from './components/MyAccount';
import ResetPassword from './components/ResetPassword';
import OAuthConsent from './pages/OAuthConsent';
import WebhookSettings from './pages/WebhookSettings';
import { useSupabaseAuth } from './contexts/SupabaseAuthContext';
import { supabase } from './lib/supabase';

// Hardcoded super admin emails - defined outside component to avoid recreating on each render
const SUPER_ADMIN_EMAILS = ['sales@traqpack.com', 'oren@traqpack.com'];

function AppRouter() {
  const { user, isAuthenticated } = useSupabaseAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUserRole = async () => {
      if (user?.id) {
        try {
          // First check if this is one of the hardcoded super admin emails
          if (user.email && SUPER_ADMIN_EMAILS.includes(user.email)) {
            const { data, error } = await supabase
              .from('user_profiles')
              .select('is_super_admin')
              .eq('id', user.id)
              .single();
            
            if (data && !error) {
              setIsSuperAdmin(data.is_super_admin || false);
            } else {
              setIsSuperAdmin(false);
            }
          } else {
            // Not the super admin email, so definitely not a super admin
            setIsSuperAdmin(false);
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          setIsSuperAdmin(false);
        }
      } else {
        setIsSuperAdmin(null);
      }
      setLoading(false);
    };

    checkUserRole();
  }, [user]);

  // Check URL path for special routes
  const currentPath = window.location.pathname;
  
  // OAuth 2.1 consent screen. Handles its own sign-in so the pending
  // authorization_id survives, so it must be checked before any auth gate.
  if (currentPath === '/oauth/consent') {
    return <OAuthConsent />;
  }

  // Handle password reset route (doesn't require authentication, uses token from email)
  if (currentPath === '/reset-password') {
    return <ResetPassword />;
  }
  
  // Handle purchase routes (these don't require authentication)
  if (currentPath === '/purchase') {
    return <PurchasePage />;
  }
  
  if (currentPath === '/purchase-success') {
    return <PurchaseSuccess />;
  }
  
  // Webhook settings (requires authentication)
  if (currentPath === '/settings/webhooks' && isAuthenticated) {
    return <WebhookSettings />;
  }

  // Handle My Account route (requires authentication)
  if (currentPath === '/account' && isAuthenticated) {
    return <MyAccount />;
  }

  // Check URL parameters for app selection
  const urlParams = new URLSearchParams(window.location.search);
  const forceWarehouse = urlParams.get('app') === 'warehouse';
  const forceMultiTenant = urlParams.get('app') === 'multitenant';

  if (loading && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Routing logic:
  // 1. If URL parameter forces a specific app, use that
  // 2. If user is super admin, show warehouse app by default
  // 3. Otherwise, show multi-tenant app
  
  if (forceWarehouse || (isSuperAdmin && !forceMultiTenant)) {
    return <AppWarehouse />;
  }
  
  return <AppMultiTenant />;
}

export default AppRouter;